import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { BookOpen, FileText, Globe, Newspaper, Plus, Trash2, ArrowUp, ArrowDown, Upload as UploadIcon } from "lucide-react";

const COLORS = { red: "#C8102E", navy: "#0A2463" };
const KIND_ICON = { file: FileText, url: Globe, news: BookOpen };
const selectCls = "mt-1.5 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm";

// ---------- Public buttons shown in the Gear hero ----------
export function PolicyLinkButtons({ links, onBanner }) {
    if (!links?.length) return null;
    return (
        <div className="mt-5 flex flex-wrap gap-3" data-testid="gear-policy-links">
            {links.map((l) => <PolicyLinkButton key={l.id} link={l} onBanner={onBanner} />)}
        </div>
    );
}

function PolicyLinkButton({ link, onBanner }) {
    const Icon = KIND_ICON[link.kind] || BookOpen;
    const cls = `inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold shadow-warm transition-all hover:scale-[1.02] ${onBanner ? "bg-white text-primary hover:bg-amber-50" : "text-white hover:brightness-110"}`;
    const style = onBanner ? {} : { backgroundColor: COLORS[link.color] || COLORS.navy };
    const body = <><Icon className="h-4 w-4" />{link.label}</>;
    const testid = `gear-policy-link-${link.id}`;
    if (link.kind === "news") {
        return <Link to={`/news/${link.news_id}`} className={cls} style={style} data-testid={testid}>{body}</Link>;
    }
    const href = link.kind === "file" ? mediaUrl(link.url) : link.url;
    return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid={testid}>{body}</a>;
}

// ---------- Admin editor dialog body ----------
export function PolicyLinksEditor({ page, onSaved, onClose }) {
    const [links, setLinks] = useState(() => (page.policy_links || []).map((l) => ({ ...l })));
    const [news, setNews] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get("/news").then(({ data }) => setNews(data || [])).catch(() => {}); }, []);

    const update = (i, patch) => setLinks((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
    const move = (i, d) => setLinks((ls) => {
        const next = [...ls];
        [next[i], next[i + d]] = [next[i + d], next[i]];
        return next;
    });
    const add = () => setLinks((ls) => [...ls, { id: "", label: "", kind: "file", url: "", news_id: "", file_name: "", color: "navy" }]);

    async function save() {
        setBusy(true);
        try {
            const { hero_image, title, subtitle, intro } = page;
            await api.put("/gear-page", { hero_image, title, subtitle, intro, policy_links: links });
            toast.success("Gear page buttons saved");
            onSaved?.();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Save failed");
        }
        setBusy(false);
    }

    return (
        <div data-testid="gear-policy-links-editor">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Gear page buttons</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mt-1">Buttons shown under the Gear page headline. Each can open an uploaded file, a website, or a News post.</p>
            <div className="space-y-3 mt-4 max-h-[60vh] overflow-y-auto pr-1">
                {links.length === 0 && <div className="text-sm text-muted-foreground text-center py-6" data-testid="gear-policy-links-empty">No buttons yet.</div>}
                {links.map((l, i) => (
                    <LinkRow key={l.id || `new-${i}`} link={l} index={i} total={links.length} news={news}
                        onChange={(patch) => update(i, patch)} onMove={(d) => move(i, d)}
                        onRemove={() => setLinks((ls) => ls.filter((_, j) => j !== i))} />
                ))}
            </div>
            <Button type="button" variant="outline" className="rounded-full mt-3" onClick={add} data-testid="gear-policy-add-btn">
                <Plus className="h-4 w-4 mr-1" />Add button
            </Button>
            <DialogFooter className="mt-5">
                <Button variant="ghost" onClick={onClose} data-testid="gear-policy-cancel-btn">Cancel</Button>
                <Button onClick={save} disabled={busy} className="rounded-full" data-testid="gear-policy-save-btn">{busy ? "Saving…" : "Save buttons"}</Button>
            </DialogFooter>
        </div>
    );
}

function LinkRow({ link, index, total, news, onChange, onMove, onRemove }) {
    const t = (s) => `gear-policy-${s}-${index}`;
    return (
        <div className="rounded-xl border border-border p-3 space-y-3 bg-card" data-testid={t("row")}>
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <Label>Button label</Label>
                    <Input value={link.label} onChange={(e) => onChange({ label: e.target.value })} className="rounded-xl mt-1.5" placeholder="e.g. Official Dress Standards" data-testid={t("label")} />
                </div>
                <Button type="button" size="icon" variant="ghost" disabled={index === 0} onClick={() => onMove(-1)} data-testid={t("up")}><ArrowUp className="h-4 w-4" /></Button>
                <Button type="button" size="icon" variant="ghost" disabled={index === total - 1} onClick={() => onMove(1)} data-testid={t("down")}><ArrowDown className="h-4 w-4" /></Button>
                <Button type="button" size="icon" variant="ghost" className="text-destructive" onClick={onRemove} data-testid={t("remove")}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Opens</Label>
                    <select value={link.kind} onChange={(e) => onChange({ kind: e.target.value, url: "", news_id: "", file_name: "" })} className={selectCls} data-testid={t("kind")}>
                        <option value="file">Uploaded file</option>
                        <option value="url">Website link</option>
                        <option value="news">News post</option>
                    </select>
                </div>
                <div>
                    <Label>Color</Label>
                    <select value={link.color} onChange={(e) => onChange({ color: e.target.value })} className={selectCls} data-testid={t("color")}>
                        <option value="navy">Navy</option>
                        <option value="red">Red</option>
                    </select>
                </div>
            </div>
            <LinkTarget link={link} news={news} onChange={onChange} t={t} />
        </div>
    );
}

function LinkTarget({ link, news, onChange, t }) {
    const [uploading, setUploading] = useState(false);

    async function upload(file) {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10 MB"); return; }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/gear/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange({ url: data.url, file_name: data.file_name });
            toast.success("File uploaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        }
        setUploading(false);
    }

    if (link.kind === "url") {
        return (
            <div>
                <Label>Web address</Label>
                <Input value={link.url} onChange={(e) => onChange({ url: e.target.value })} className="rounded-xl mt-1.5" placeholder="https://" data-testid={t("url")} />
            </div>
        );
    }
    if (link.kind === "news") {
        return (
            <div>
                <Label className="flex items-center gap-1"><Newspaper className="h-3.5 w-3.5" />News post</Label>
                <select value={link.news_id} onChange={(e) => onChange({ news_id: e.target.value })} className={selectCls} data-testid={t("news")}>
                    <option value="">Choose a post…</option>
                    {link.news_id && !news.some((n) => n.id === link.news_id) && <option value={link.news_id}>Current post</option>}
                    {news.map((n) => <option key={n.id} value={n.id}>{n.title}</option>)}
                </select>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-3 flex-wrap">
            <label className="cursor-pointer">
                <Button asChild variant="outline" size="sm" className="rounded-full" type="button">
                    <span data-testid={t("upload")}><UploadIcon className="h-3.5 w-3.5 mr-1" />{uploading ? "Uploading…" : link.url ? "Replace file" : "Upload image or PDF"}</span>
                </Button>
                <input type="file" accept="image/*,application/pdf" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} data-testid={t("file-input")} />
            </label>
            {link.url && (
                <a href={mediaUrl(link.url)} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline truncate max-w-[220px]" data-testid={t("file-name")}>
                    {link.file_name || "View file"}
                </a>
            )}
        </div>
    );
}
